<template>
  <div class="Comments-Form">
    <textarea class="Comments-Form__input"
              placeholder="Ваш комментарий..."
              autocomplete="off" spellcheck="true"
              autocapitalize="off"
              v-model="textData"
              ref="textarea"
              @input="toInput"
              @focus="toFocus"
              @blur="toBlur"
              data-parent-comments-form-field
    />
    <div class="Comments-Form__img-item" v-if="previewImage">
      <LoaderCircle class="Comments-Form__ldr" :color="'white'" :size="16" v-if="isLoadingImg"/>
      <button class="Comments-Form__remove-img" v-if="!isLoadingImg" @click="toRemoveImage">
        <CrossIcon/>
      </button>
      <img class="Comments-Form__img" :src="previewImage" :class="get_loadingImgClass"/>
    </div>
    <div class="Comments-Form__ftr">
      <div class="Comments-Form__ftr-group">
        <div class="Comments-Form__ftr-option Comments-Form__ftr-option--smile">
          <EditorSmilesModule @smile="handleSmile" @clickTrigger="handleClickTrigger"/>
        </div>
        <div class="Comments-Form__ftr-option" @click="openFileManager">
          <AddImageIcon/>
          <input type="file" accept=".jpg,.jpeg,.png,.webp" ref="imageInput" @change="handleFileChange" v-show="false"/>
        </div>
        <div class="Comments-Form__ftr-option">
          <EditorGifModule @image="addGif"/>
        </div>
      </div>
      <div class="Comments-Form__ftr-group">
        <span class="Comments-Form__counter">{{ get_textDataLength }} / {{ MAX_SYMBOLS }}</span>
        <button class="Comments-Form__btn" :disabled="get_isDisabled" @click="toStartToSendComment">{{ get_btnTxt }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {onMounted, computed, ref, nextTick, defineProps, onBeforeMount} from 'vue';
import {Props} from './scripts/props.js';
import {useStore} from 'vuex';
import {toPostComment} from './api/toPostComment';
import {toEditComment} from './api/toEditComment.js';
import {loadImageToServer} from '@shared/api/images/loadImageToServer.js';
//----------------------------
import AddImageIcon from '@svg/AddImageIcon.vue';
import LoaderCircle from '@ui/Loaders/LoaderCircle/LoaderCircle.vue';
import CrossIcon from '@svg/CrossIcon.vue';
import EditorSmilesModule from '@entities/Editor/EditorSmilesModule/EditorSmilesModule.vue';
import EditorGifModule from '@entities/Editor/EditorGifModule/EditorGifModule.vue';
//----------------------------
const store = useStore();
const props = defineProps(Props());
//----------------------------
const MAX_SYMBOLS = 1000;
//----------------------------
const textData = ref('');
const isLoading = ref(false);
const textarea = ref(null);
const imageInput = ref(null);
const previewImage = ref(null);
const uploadedImageUrl = ref(null);
const isLoadingImg = ref(null);
const isChangeImg = ref(null);
const objectData = ref(null);
const context = ref(null);
const isButtonDisabled = ref(false);
//----------------------------
onBeforeMount(() => {
  if (props.context === 'infonoise') {
    objectData.value = store.state.ContentSystem.Alpha.list[props.postId] || store.state.ContentSystem.Infonoise.list[props.postId];
  } else if (props.context === 'signal') {
    objectData.value = store.state.Signals.data[props.postId];
  }
})
onMounted(async () => {
  if (get_isEditCommentMode.value){
    textData.value = get_initText.value.replace(/\\n/g, '\n');
    await nextTick();
    setTextareaHeight();
  }
  if (get_comment.value && get_isEditCommentMode.value) {
    if (get_comment.value.image_url) {
      previewImage.value = get_comment.value.image_url;
      uploadedImageUrl.value = get_comment.value.image_url;
    }
  }
  toInitFocus();
  addPasteListener();
})
//----------------------------
//Получить контекст объекта для комментариев
const get_commentsCtx = computed(() => {
  if (props.commentsPlace === 'popup') return 'popupComments';
  else if (props.commentsPlace === 'page') return 'contentPageComments';
});
//Комментарий находится в режиме редактирования?
const get_isEditCommentMode = computed(() => store.state.ContentSystem.Comments[get_commentsCtx.value].isEditMode);
//Получить начальны текст коммента для редактирования
const get_initText = computed(() => store.state.ContentSystem.Comments[get_commentsCtx.value].list.find(item => item.id === props.selfId).comment);
//Получить комментарий
const get_comment = computed(() => store.state.ContentSystem.Comments[get_commentsCtx.value].list.find(item => item.id === props.selfId))
//Получить кол-во символов
const get_textDataLength = computed(() => textData.value.length);
//Кнопка заблокирована?
const get_isDisabled = computed(() => {
  return isButtonDisabled.value || (get_textDataLength.value <= 0 && !isChangeImg.value && !isLoadingImg.value);
});
//Получить текст кнопки
const get_btnTxt = computed(() => get_isEditCommentMode.value && props.selfId ? 'Сохранить' : 'Добавить');
//Добавить класс к картинке на время загрузки ее на сервер
const get_loadingImgClass = computed(() => isLoadingImg.value ? 'Comments-Form__img--loading' : '');
//----------------------------
//Ввод данных в поле
const toInput = async () => {
  checkCharacterLimit();
  await nextTick();
  setTextareaHeight();
}
//Проверить на кол-во символов
const checkCharacterLimit = () => {
  if (get_textDataLength.value <= MAX_SYMBOLS) return;
  textData.value = textData.value.slice(0, MAX_SYMBOLS);
};
//Рассчитать высоту поля
const setTextareaHeight = () => {
  if (!textarea.value) return;
  textarea.value.style.maxHeight = '';
  const currentFieldSize = textarea.value.scrollHeight;
  textarea.value.style.maxHeight = `${currentFieldSize}px`;
  textarea.value.style.height = `${currentFieldSize}px`;
};
//Начать процесс отправки коммента на сервер
const toStartToSendComment = async () => {
  if (get_isDisabled.value) return;

  isButtonDisabled.value = true;

  try {
    await (get_isEditCommentMode.value ? editComment() : sendComment());
  } finally {
    isButtonDisabled.value = false;
  }
};

//Отправить данные на сервер
const sendComment = async () => {
  try {
    toToggleLoading(true);
    const data = toPrepareData();
    const result = await toPostComment(data, props.context);
    clearField();
    toAddNewComment(result);
    toUpdateLastCommentInPost(result);
  } catch (error) {
    console.error('Ошибка при отправке комментария', error);
  } finally {
    toToggleLoading(false);
  }
}
//Редактировать комментарий
const editComment = async () => {
  try {
    toToggleLoading(true);
    const data = toPrepareData();

    const result = await toEditComment(data, props.context);
    closeEditor();
    toEditCommentItem(result);
  } catch (error) {
    console.error('Ошибка при изенении комментария', error);
  } finally {
    toToggleLoading(false);
  }
}
//Подготовить данные для отправки на сервер
const toPrepareData = () => {

  const data = {
    object_pk: props.postId,
    comment: textData.value,
    content_type: objectData.value.content_type,
  }
  if (props.selfId) data['parent_id'] = props.selfId;
  data['image_url'] = (uploadedImageUrl.value) ? uploadedImageUrl.value : '';
  return data;
}
//Добавить новый комментарий
const toAddNewComment = (comment) => {
  store.dispatch('ContentSystem/Comments/addNewComment', {comment: comment, context: get_commentsCtx.value});
  objectData.value.comments_count++;
  const isMine = comment.user.username === store.state.UserSystem.user.username;
  if (!isMine) return;
  objectData.value.init_comments_count++;
}
//Обновить последний комментарий к посту
const toUpdateLastCommentInPost = (comment) => {
  if (!objectData.value) return;
  objectData.value.last_comment = comment;
}
//Переключить состояние процесса отправки комментария
const toToggleLoading = (status) => isLoading.value = status;
//Очистить поле ввода
const clearField = async () => {
  textData.value = '';
  await nextTick();
  toRemoveImage();
  isChangeImg.value = false;
  setTextareaHeight();
}
//Вставить смайлик в позицию каретки
const handleSmile = async (smile) => {
  const cursorPos = textarea.value.selectionStart;
  const currentText = textData.value;
  textData.value = currentText.slice(0, cursorPos) + smile + currentText.slice(cursorPos);
  await nextTick();
  const newCursorPos = cursorPos + smile.length;
  textarea.value.selectionStart = textarea.value.selectionEnd = newCursorPos;
  textarea.value.focus();
  setTextareaHeight();
};
//Обработчик для эмита клика, сохраняющий фокус
const handleClickTrigger = async () => {
  await nextTick();
  textarea.value.focus();
  setTextareaHeight();
};
const toFocus = () => store.commit('FormsInterfaceSystem/toToggleFocusFormTextElement', true);
//Потеря фокуса на поле
const toBlur = () => store.commit('FormsInterfaceSystem/toToggleFocusFormTextElement', false);
//Закрыть редактор (для редактирования)
const closeEditor = () => {
  store.commit('ContentSystem/Comments/setChildFormCommentId', {
    id: null,
    isEdit: false,
    context: get_commentsCtx.value
  });
}
//Изменить объект комментария после редактирования
const toEditCommentItem = (data) => {
  store.commit('ContentSystem/Comments/toEditCommentObject', {
    data: data.comments,
    context: get_commentsCtx.value
  });
}
//Убрать фокус по форме при инициализации после отрисовки
const toInitFocus = () => {
  if (props.isFocus) {
    textarea.value.focus();
  }
}
//Открыть файловый менеджер для выбора картинки
const openFileManager = () => imageInput.value.click();
//Обработка выбора файла
const handleFileChange = async (event) => {
  const file = event.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (e) => previewImage.value = e.target.result;
  reader.readAsDataURL(file);
  toToggleLoadingImg(true);
  const response = await loadImageToServer(file);
  toToggleLoadingImg(false);
  uploadedImageUrl.value = response.image_url;
  isChangeImg.value = true;
};
//Переключить статус загрузки изображения на сервер
const toToggleLoadingImg = (status) => isLoadingImg.value = status;
//Убрать изображение из формы
const toRemoveImage = () => {
  previewImage.value = null;
  uploadedImageUrl.value = null;
  isChangeImg.value = true;
}
//Добавить гифку
const addGif = (img) => {
  previewImage.value = img;
  uploadedImageUrl.value = img;
}

// Добавление картинки из буфера
const addPasteListener = () => {
  if (!textarea.value) return;

  textarea.value.addEventListener("paste", async (event) => {
    const items = event.clipboardData.items;

    for (let item of items) {
      if (item.type.indexOf("image") !== -1) {
        event.preventDefault();

        const file = item.getAsFile();
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => previewImage.value = e.target.result;
        reader.readAsDataURL(file);

        toToggleLoadingImg(true);
        const response = await loadImageToServer(file);
        toToggleLoadingImg(false);

        // Перезаписываем старое изображение
        previewImage.value = response.image_url;
        uploadedImageUrl.value = response.image_url;
        isChangeImg.value = true;
        break;
      }
    }
  });
};

</script>

<style lang="scss">
@import './styles/_styles';
</style>
